import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Form, Table, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';

const BuscadorSatModal = ({ 
    show, 
    onHide, 
    onSelect, 
    tipo
}) => {
    const [busqueda, setBusqueda] = useState('');
    const [resultados, setResultados] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [pagina, setPagina] = useState(1);
    const [tieneMasResultados, setTieneMasResultados] = useState(true);
    const [showNuevoForm, setShowNuevoForm] = useState(false);
    const [nuevoRegistro, setNuevoRegistro] = useState({
        id: null,
        codigo: '',
        descripcion: ''
    });
    const [modoEdicion, setModoEdicion] = useState(false);
    const tableRef = useRef(null);
    const { token } = useToken();
    const { showErrorToast, showSuccessToast } = useToast();
    const buscar = async (paginaActual = 1, esNuevaBusqueda = false) => {
        // Si es una búsqueda con valor vac&iacute;o (carga inicial), usar la función espec&iacute;fica
        if (busqueda.trim().length === 0 && paginaActual === 1) {
            await cargarRegistrosIniciales();
            return;
        }

        // Para búsquedas con texto, requerir al menos 2 caracteres
        if (busqueda.trim().length > 0 && busqueda.trim().length < 2) return;
        
        setCargando(true);
        try {
            const response = await fetch('/api/Productos/BuscarCatalogoSat', {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    tipo: tipo,
                    valor: busqueda.trim(),
                    pagina: paginaActual,
                    registrosPorPagina: 10
                })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1) {
                    const nuevosResultados = data.detalles || [];
                    
                    if (esNuevaBusqueda) {
                        setResultados(nuevosResultados);
                        setTieneMasResultados(nuevosResultados.length === 10);
                    } else {
                        setResultados(prev => [...prev, ...nuevosResultados]);
                        setTieneMasResultados(nuevosResultados.length === 10);
                    }
                } else {
                    if (esNuevaBusqueda) {
                        setResultados([]);
                        setTieneMasResultados(false);
                    }
                    throw new Error(data.vMessage || 'No se encontraron resultados');
                }
            } else {
                throw new Error('Error en la petición');
            }
        } catch (error) {
            if (esNuevaBusqueda) {
                
                showErrorToast('Error al buscar: ' + error.message);
            }
        } finally {
            setCargando(false);
        }
    };

    const handleNuevaBusqueda = () => {
        setPagina(1);
        setResultados([]);
        setTieneMasResultados(true);
        buscar(1, true);
    };

    const cargarMasResultados = useCallback(() => {
        if (!cargando && tieneMasResultados && resultados.length > 0) {
            const nuevaPagina = pagina + 1;
            setPagina(nuevaPagina);
            buscar(nuevaPagina, false);
        }
    }, [cargando, tieneMasResultados, pagina, resultados.length]);

    const handleScroll = useCallback(() => {
        if (tableRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = tableRef.current;
            if (scrollTop + clientHeight >= scrollHeight - 10) {
                cargarMasResultados();
            }
        }
    }, [cargarMasResultados]);

    useEffect(() => {
        const tableElement = tableRef.current;
        if (tableElement) {
            tableElement.addEventListener('scroll', handleScroll);
            return () => tableElement.removeEventListener('scroll', handleScroll);
        }
    }, [handleScroll]);

    useEffect(() => {
        if (show) {
            setBusqueda('');
            setResultados([]);
            setPagina(1);
            setTieneMasResultados(true);
            // Cargar los primeros 10 registros al abrir el modal
            cargarRegistrosIniciales();
        }
    }, [show]);

    const cargarRegistrosIniciales = async () => {
        setCargando(true);
        try {
            const response = await fetch('/api/Productos/BuscarCatalogoSat', {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    tipo: tipo,
                    valor: '', // Valor vacío para obtener todos los registros
                    pagina: 1,
                    registrosPorPagina: 10
                })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1) {
                    const nuevosResultados = data.detalles || [];
                    setResultados(nuevosResultados);
                    setTieneMasResultados(nuevosResultados.length === 10);
                } else {
                    setResultados([]);
                    setTieneMasResultados(false);
                }
            } else {
                setResultados([]);
                setTieneMasResultados(false);
            }
        } catch (error) {
            setResultados([]);
            setTieneMasResultados(false);
        } finally {
            setCargando(false);
        }
    };

    const guardarNuevo = async () => {
        if (!nuevoRegistro.codigo || !nuevoRegistro.descripcion) {
            
            showErrorToast('Todos los campos son obligatorios');
            return;
        }

        setCargando(true);
        try {
            const endpoint = tipo === 'producto' 
                ? '/api/Productos/GuardarCodigoSat'
                : '/api/Productos/GuardarClaveUnidad';

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    id: nuevoRegistro.id,
                    codigo: nuevoRegistro.codigo,
                    descripcion: nuevoRegistro.descripcion
                })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1) {
                    // Crear el objeto del registro guardado
                    const registroGuardado = {
                        id: data.detalles ? parseInt(data.detalles) : null,
                        codigo: nuevoRegistro.codigo,
                        descripcion: nuevoRegistro.descripcion
                    };

                    // Agregar o actualizar el registro en la lista de resultados
                    if (modoEdicion) {
                        // Actualizar registro existente usando el id
                        setResultados(prev => prev.map(item => 
                            item.id === nuevoRegistro.id 
                                ? { ...item, codigo: nuevoRegistro.codigo, descripcion: nuevoRegistro.descripcion }
                                : item
                        ));
                    } else {
                        // Agregar nuevo registro al inicio de la lista
                        setResultados(prev => [registroGuardado, ...prev]);
                    }

                    // Resetear el formulario y mostrar mensaje de éxito
                    setShowNuevoForm(false);
                    setNuevoRegistro({
                        id: null,
                        codigo: '',
                        descripcion: ''
                    });
                    setModoEdicion(false);

                    // Mostrar mensaje informativo
                    showSuccessToast(data.vMessage);
                    
                } else {
                    throw new Error(data.vMessage || 'Error al guardar');
                }
            } else {
                throw new Error('Error en la petición');
            }
        } catch (error) {
            
            showErrorToast('Error al guardar: ' + error.message);
        } finally {
            setCargando(false);
        }
    };

    const editarRegistro = (item) => {
        setNuevoRegistro({
            id: item.id || null, // Usar el id del item si está disponible
            codigo: item.codigo,
            descripcion: item.descripcion
        });
        setModoEdicion(true);
        setShowNuevoForm(true);
    };


    return (
        <Modal show={show} onHide={onHide} size="lg" centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title>
                    {showNuevoForm 
                        ? (modoEdicion ? 'Editar Registro' : 'Agregar Nuevo Registro')
                        : (tipo === 'producto' ? 'Buscar C\u00F3digo de Producto/Servicio' : 'Buscar Clave de Unidad')
                    }
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {!showNuevoForm ? (
                    <>
                        <div className="d-flex gap-2 mb-3">
                            <Form.Control
                                type="text"
                                placeholder="Buscar por código o descripción..."
                                value={busqueda}
                                onChange={(e) => setBusqueda(e.target.value.toUpperCase())}
                                onKeyPress={(e) => e.key === 'Enter' && handleNuevaBusqueda()}
                            />
                            <Button
                                variant="primary"
                                onClick={handleNuevaBusqueda}
                                disabled={cargando}
                                style={{ flexShrink: 0 }}
                            >
                                {cargando ? (
                                    <Spinner size="sm" />
                                ) : (
                                    <FontAwesomeIcon icon="search" />
                                )}
                            </Button>
                        </div>

                        <div 
                            ref={tableRef}
                            style={{ maxHeight: '50vh', overflowY: 'auto' }}
                        >
                            <Table hover responsive>
                                <thead className="bg-light sticky-top">
                                    <tr>
                                        <th>C&oacute;digo</th>
                                        <th>Descripci&oacute;n</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {resultados.map((item, index) => (
                                        <tr key={`${item.codigo}-${index}`}>
                                            <td>{item.codigo}</td>
                                            <td>{item.descripcion}</td>
                                            <td>
                                                <Button 
                                                    variant="falcon-default" 
                                                    size="sm"
                                                    onClick={() => onSelect(item)}
                                                    className="me-1"
                                                >
                                                    <FontAwesomeIcon icon="check" className="text-success" />
                                                </Button>
                                                <Button 
                                                    variant="falcon-default" 
                                                    size="sm"
                                                    onClick={() => editarRegistro(item)}
                                                >
                                                    <FontAwesomeIcon icon="edit" className="text-primary" />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                    {cargando && resultados.length > 0 && (
                                        <tr>
                                            <td colSpan="3" className="text-center py-3">
                                                <Spinner size="sm" className="me-2" />
                                                Cargando más resultados...
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                            
                            {resultados.length === 0 && !cargando && busqueda.trim() && (
                                <div className="text-center py-4 text-muted">
                                    No se encontraron resultados para "{busqueda}"
                                </div>
                            )}
                        </div>

                        <div className="text-center mt-3">
                            <Button 
                                variant="link" 
                                onClick={() => setShowNuevoForm(true)}
                            >
                                <FontAwesomeIcon icon="plus" className="me-1" />
                                Agregar Nuevo
                            </Button>
                        </div>
                    </>
                ) : (
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>C&oacute;digo</Form.Label>
                            <Form.Control
                                type="text"
                                value={nuevoRegistro.codigo}
                                onChange={(e) => setNuevoRegistro({
                                    ...nuevoRegistro,
                                    codigo: e.target.value
                                })}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Descripci&oacute;n</Form.Label>
                            <Form.Control
                                type="text"
                                value={nuevoRegistro.descripcion}
                                onChange={(e) => setNuevoRegistro({
                                    ...nuevoRegistro,
                                    descripcion: e.target.value
                                })}
                            />
                        </Form.Group>

                        <div className="d-flex justify-content-end gap-2">
                            <Button 
                                variant="secondary"
                                onClick={() => setShowNuevoForm(false)}
                            >
                                Cancelar
                            </Button>
                            <Button 
                                variant="primary"
                                onClick={guardarNuevo}
                                disabled={cargando}
                            >
                                {cargando ? (
                                    <>
                                        <Spinner size="sm" className="me-1" />
                                        Guardando...
                                    </>
                                ) : 'Guardar'}
                            </Button>
                        </div>
                    </Form>
                )}
            </Modal.Body>
        </Modal>
    );
};

BuscadorSatModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
    tipo: PropTypes.oneOf(['producto', 'unidad']).isRequired
};

export default BuscadorSatModal;